import React from "react";
import { FileText, Sparkles, Shield, ScrollText, Mail } from "lucide-react";

function Terms() {
  return (
    <div className="min-h-screen bg-[#05070c] text-white">

      {/* soft background glow (matches your new pages) */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute bottom-[-220px] right-[-160px] h-[520px] w-[520px] rounded-full bg-sky-500/6 blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/40" />
      </div>

      <main className="px-3 sm:px-6 md:px-8 pt-6 pb-12">
        <div className="mx-auto max-w-6xl">
          {/* Outer framed panel (same “About AnimeHouse” vibe) */}
          <section className="rounded-[28px] border border-white/12 bg-white/4 shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_12px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl">
            {/* Header */}
            <div className="px-5 sm:px-8 py-6 sm:py-7 border-b border-white/10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl border border-white/12 bg-white/5">
                    <FileText className="text-emerald-300" size={22} />
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                        Terms of{" "}
                        <span className="text-emerald-300 drop-shadow-[0_0_18px_rgba(16,185,129,0.15)]">
                          Service
                        </span>
                      </h1>

                      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/5 px-3 py-1 text-[11px] font-semibold text-white/70">
                        <Sparkles className="h-3.5 w-3.5 text-emerald-200" />
                        Legal
                      </span>
                    </div>

                    <p className="mt-1 text-sm text-white/65">
                      Last Updated: <span className="text-white/85">October 2025</span>
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Account", "Content", "Safety", "Privacy"].map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs font-semibold text-white/75"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-2xl border border-white/12 bg-white/5 px-3 py-2 text-xs font-semibold text-white/70">
                    <Shield className="h-4 w-4 text-emerald-200" />
                    Community-first
                  </span>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-emerald-400/18 bg-emerald-500/8 px-4 py-3 text-sm text-emerald-100/80">
                By using AnimeHouse, you agree to these Terms. If you don’t agree, please don’t use the platform.
              </div>
            </div>

            {/* Body */}
            <div className="px-5 sm:px-8 py-6 sm:py-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Main Terms */}
                <div className="lg:col-span-8 space-y-4">
                  <Section
                    title="Agreement to Terms"
                    icon={<ScrollText className="h-4 w-4 text-emerald-200" />}
                  >
                    By accessing or using AnimeHouse ("Service", "Application", "Platform"),
                    you agree to be bound by these Terms of Service ("Terms"). If you disagree
                    with any part of these Terms, you may not access the Service.
                  </Section>

                  <Section
                    title="Eligibility"
                    icon={<Shield className="h-4 w-4 text-emerald-200" />}
                  >
                    You must be at least 13 years old to use this Service. By using the Service,
                    you represent and warrant that you meet this age requirement.
                  </Section>

                  <Section
                    title="User Accounts"
                    icon={<Sparkles className="h-4 w-4 text-emerald-200" />}
                  >
                    <Subhead>Registration</Subhead>
                    <List
                      items={[
                        "Provide accurate and complete information when creating an account",
                        "Maintain the confidentiality of your login credentials",
                        "You are responsible for all activity under your account",
                        "Notify us immediately of any unauthorized use",
                      ]}
                    />

                    <Subhead>Account Security</Subhead>
                    <List
                      items={[
                        "Use a strong password and keep it private",
                        "Do not share credentials with others",
                        "We are not liable for loss resulting from compromised credentials",
                      ]}
                    />
                  </Section>

                  <Section
                    title="Acceptable Use"
                    icon={<Shield className="h-4 w-4 text-emerald-200" />}
                  >
                    <p className="text-white/70">
                      You agree <span className="text-white font-semibold">NOT</span> to:
                    </p>
                    <List
                      items={[
                        "Violate laws or regulations",
                        "Infringe intellectual property rights",
                        "Post offensive, threatening, or harassing content",
                        "Spam, bot, or automate interactions",
                        "Attempt unauthorized access to systems",
                        "Upload malicious code or harmful software",
                        "Impersonate another person or entity",
                        "Manipulate ratings or reviews",
                        "Scrape or harvest data from the Service",
                        "Interfere with the Service’s stability or performance",
                      ]}
                    />
                  </Section>

                  <Section
                    title="Content"
                    icon={<Sparkles className="h-4 w-4 text-emerald-200" />}
                  >
                    <Subhead>User-Generated Content</Subhead>
                    <List
                      items={[
                        "You retain ownership of content you post",
                        "By posting, you grant us a license to host and display your content for platform functionality",
                        "You are responsible for the content you post",
                        "We may remove content that violates these Terms",
                      ]}
                    />

                    <Subhead>Prohibited Content</Subhead>
                    <p className="text-white/70">You may not post content that:</p>
                    <List
                      items={[
                        "Is illegal, obscene, or defamatory",
                        "Infringes on copyrights or trademarks",
                        "Shares private personal info without consent",
                        "Promotes violence, discrimination, or hate speech",
                        "Is sexually explicit or inappropriate",
                        "Contains spam or commercial solicitation",
                      ]}
                    />

                    <Subhead>Anime Content</Subhead>
                    <List
                      items={[
                        "Anime titles, images, and info belong to their respective owners",
                        "We provide info for discovery/review and do not claim ownership",
                        "Users should respect copyright when sharing/discussing content",
                      ]}
                    />
                  </Section>

                  <Section
                    title="Reviews and Ratings"
                    icon={<Sparkles className="h-4 w-4 text-emerald-200" />}
                  >
                    <List
                      items={[
                        "Reviews should be honest and based on real experience",
                        "Do not post fake reviews or manipulate ratings",
                        "Do not post for compensation without disclosure",
                        "We may remove reviews that violate guidelines",
                        "Reviews reflect user opinions, not AnimeHouse",
                      ]}
                    />
                  </Section>

                  <Section
                    title="Privacy"
                    icon={<Shield className="h-4 w-4 text-emerald-200" />}
                  >
                    Your use of the Service is also governed by our{" "}
                    <a
                      href="/privacy"
                      className="text-emerald-200 hover:underline underline-offset-4"
                    >
                      Privacy Policy
                    </a>
                    .
                  </Section>

                  <Section
                    title="Termination"
                    icon={<Shield className="h-4 w-4 text-emerald-200" />}
                  >
                    <Subhead>By You</Subhead>
                    <p className="text-white/70">
                      You may terminate your account at any time by contacting us or using the
                      account deletion feature.
                    </p>

                    <Subhead>By Us</Subhead>
                    <p className="text-white/70">We may suspend or terminate your account if you:</p>
                    <List
                      items={[
                        "Violate these Terms",
                        "Engage in fraudulent or illegal activity",
                        "Abuse the Service or other users",
                        "Remain inactive for an extended period",
                      ]}
                    />
                  </Section>

                  <Section
                    title="Disclaimers"
                    icon={<Shield className="h-4 w-4 text-emerald-200" />}
                  >
                    The Service is provided "as is" without warranties of any kind. We do not
                    guarantee the Service will be error-free, secure, or uninterrupted.
                  </Section>

                  <Section
                    title="Limitation of Liability"
                    icon={<Shield className="h-4 w-4 text-emerald-200" />}
                  >
                    To the maximum extent permitted by law, we are not liable for indirect,
                    incidental, or consequential damages. Our total liability will not exceed the
                    amount you paid to use the Service (if any).
                  </Section>

                  <Section
                    title="Changes to Terms"
                    icon={<Sparkles className="h-4 w-4 text-emerald-200" />}
                  >
                    We may update these Terms at any time. We’ll post updates here and change the
                    "Last Updated" date. Continued use means you accept the updated Terms.
                  </Section>

                  {/* Acknowledgment callout */}
                  <div className="rounded-3xl border border-emerald-400/18 bg-emerald-500/8 p-5 sm:p-6">
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
                        <Shield className="h-5 w-5 text-emerald-200" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-base font-semibold text-white/90">Acknowledgment</p>
                        <p className="mt-1 text-sm text-white/70 leading-relaxed">
                          By using AnimeHouse, you acknowledge that you have read, understood, and
                          agree to be bound by these Terms of Service and our Privacy Policy.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit">
                  <div className="rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
                        <Mail className="h-5 w-5 text-emerald-200" />
                      </div>
                      <p className="text-sm font-semibold text-emerald-200">Contact</p>
                    </div>

                    <p className="mt-3 text-sm text-white/65 leading-relaxed">
                      Questions about these Terms?
                    </p>

                    <a
                      href="mailto:animehousewebsite@gmail.com"
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/5 px-4 py-3 text-sm font-semibold text-white/85 hover:bg-white/8 transition"
                    >
                      <Mail className="h-4 w-4 text-emerald-200" />
                      animehousewebsite@gmail.com
                    </a>

                    <div className="mt-5 space-y-2 text-sm">
                      <a
                        href="/privacy"
                        className="block rounded-2xl border border-white/10 bg-white/4 px-4 py-3 text-white/65 hover:text-white hover:bg-white/6 transition"
                      >
                        Privacy Policy
                      </a>
                      <a
                        href="/community"
                        className="block rounded-2xl border border-white/10 bg-white/4 px-4 py-3 text-white/65 hover:text-white hover:bg-white/6 transition"
                      >
                        Back to Community
                      </a>
                    </div>
                  </div>

                  <div className="mt-4 text-center text-xs text-white/35">
                    © {new Date().getFullYear()} AnimeHouse
                  </div>
                </aside>
              </div>
            </div>

            {/* Footer strip */}
            <div className="px-6 sm:px-8 py-4 border-t border-white/10 text-center text-sm text-white/60">
              Thank you for being a part of the{" "}
              <span className="text-emerald-200 font-semibold">AnimeHouse</span> community!
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default Terms;

/* -------------------- UI atoms (kept clean & modern) -------------------- */

function Section({ title, icon, children }) {
  return (
    <section className="rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
          {icon}
        </div>
        <h2 className="text-lg font-bold text-emerald-200">{title}</h2>
      </div>

      <div className="mt-4 text-sm text-white/75 leading-relaxed">{children}</div>
    </section>
  );
}

function Subhead({ children }) {
  return <h3 className="mt-4 mb-2 text-sm font-semibold text-white/90">{children}</h3>;
}

function List({ items }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-300/60 shrink-0" />
          <span className="text-sm text-white/70 leading-relaxed">{t}</span>
        </li>
      ))}
    </ul>
  );
}
